import { ChevronRight } from 'lucide-react'
import React, { useState } from 'react'

import Tooltip from 'components/reusable/Tooltip'

interface LedgerPathDisplayProps {
  path: string
  maxLength?: number
  showOnlyLastSegment?: boolean
}

export const LedgerPathDisplay: React.FC<LedgerPathDisplayProps> = ({
  path,
  maxLength = 60,
  showOnlyLastSegment = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const parts = path.split('.')

  // In tree view mode, only show the last segment
  if (showOnlyLastSegment) {
    const lastSegment = parts[parts.length - 1]
    return (
      <Tooltip triggerChildren={
        <span className='text-sm text-gray-700'>{lastSegment}</span>
      }
      >
        <div className='text-xs p-2 max-w-md break-all'>
          Full path: {path}
        </div>
      </Tooltip>
    )
  }

  // If path is short enough, display it normally
  if (path.length <= maxLength) {
    return (
      <div className='flex items-center gap-1 text-sm'>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight size={14} className='text-gray-400 flex-shrink-0' />}
            <span className='text-gray-700'>{part}</span>
          </React.Fragment>
        ))}
      </div>
    )
  }

  // For long paths, show truncated version with expand option
  if (!isExpanded && parts.length > 3) {
    const firstPart = parts[0]
    const lastParts = parts.slice(-2)
    const hiddenCount = parts.length - 3

    return (
      <div className='flex items-center gap-1 text-sm'>
        <span className='text-gray-700'>{firstPart}</span>
        <ChevronRight size={14} className='text-gray-400 flex-shrink-0' />
        <Tooltip triggerChildren={
          <button
            onClick={() => setIsExpanded(true)}
            className='text-blue-600 hover:text-blue-800 font-medium'
          >
            ... ({hiddenCount} levels)
          </button>
        }
        >
          <div className='text-xs p-2 max-w-md break-all'>
            {path}
          </div>
        </Tooltip>
        <ChevronRight size={14} className='text-gray-400 flex-shrink-0' />
        {lastParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight size={14} className='text-gray-400 flex-shrink-0' />}
            <span className='text-gray-700'>{part}</span>
          </React.Fragment>
        ))}
      </div>
    )
  }

  // Expanded view or short multi-part path
  return (
    <div className='flex items-center gap-1 text-sm flex-wrap'>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && <ChevronRight size={14} className='text-gray-400 flex-shrink-0' />}
          <span className='text-gray-700'>{part}</span>
        </React.Fragment>
      ))}
      {isExpanded && (
        <button
          onClick={() => setIsExpanded(false)}
          className='ml-2 text-blue-600 hover:text-blue-800 text-xs'
        >
          (collapse)
        </button>
      )}
    </div>
  )
}

export default LedgerPathDisplay
